// LG-97 — the one store in the app with no bottom.
//
// `setAsideLiveDraft` writes a NEW timestamped slot every time a draft is
// set aside and removes nothing, and the bell only ever offers
// slots with at least one completed set (`quarantineStore.ts` isRecoverable,
// "Left on disk, not surfaced"). A slot with no completed work is therefore
// invisible AND unreachable: no gesture in the app can delete it, ever. On
// web-rnw it is worse — that mount calls the writer and has no recovery
// surface at all (`apps/web-rnw/src/useSessionLoader.ts:90`), so nothing over
// there is deletable by anybody.
//
// The decision lives here, pure, beside `quarantinePrefix` and
// `parsePersistedBlob` for the reason that file's header already gives: the
// 2026-06-30 toxic-draft incidents are exactly this code class, and a second
// copy that can drift is the bug those incidents were about. The two storage
// twins (AsyncStorage and localStorage) do the IO and share this answer.
//
// ⛔ What it will NOT delete, and why each one is load-bearing:
//  - **a slot carrying completed sets.** That is recoverable work — Leonardo's
//    lost workout, 2026-06-30 — and the athlete owns the discard. The durable
//    rule is not losing data irreversibly, and this is the only case here
//    where the loss would be real.
//  - **the most recent slot**, whatever it holds. It is the one a support
//    request would be about, and "the last thing that went wrong" is worth a
//    few kilobytes forever.

/** One quarantine slot, already read and summarised by the caller. */
export type QuarantineSlot = {
  /** The storage key, which is what the caller will remove. */
  key: string;
  /** ISO stamp parsed off the key suffix. */
  quarantinedAt: string;
  /** Does this slot hold a set the athlete actually completed?
   *  `hasCommittedWork(state.draft)` — ⛔ not a third definition of "worth
   *  keeping": it is the predicate the persisted blob's own `inProgress` flag
   *  is written from (`loggerDraftStorage.ts:60`).
   *  ⚠️ The recovery card asks the SAME question through a different function
   *  (`summarizeDraft().completedCount > 0`, `quarantineStore.ts:46`). They
   *  agree today and there is no bug in it, but they are two copies of one
   *  concept and the next hand in that file should collapse them (`CN-44`). */
  hasCommittedWork: boolean;
};

/** How many slots survive regardless of age. Five is enough to hold the
 *  cluster a single bad afternoon produces, which is the shape these arrive
 *  in — they come from one device having one problem repeatedly, not from a
 *  steady drip. */
export const QUARANTINE_KEEP = 5;

/** After this, a slot with nothing in it is just bytes. Same 30 the server's
 *  tombstones use (`apps/api/src/lib/sessionMutations.ts:29`). */
export const QUARANTINE_TTL_DAYS = 30;

const DAY_MS = 86_400_000;

/**
 * Which slots to delete. Returns keys, never mutates, and is safe to call with
 * anything the storage layer managed to read.
 *
 * An unparseable timestamp is treated as old, not as an error: the key suffix
 * is written by us, so a bad one means a slot from a shape we no longer
 * understand. It still cannot be reaped unless it also carries no work and is
 * not the newest, which is the same protection everything else gets.
 */
export function quarantineSlotsToReap(
  slots: readonly QuarantineSlot[],
  now: Date,
  opts: { keep?: number; ttlDays?: number } = {},
): string[] {
  const keep = opts.keep ?? QUARANTINE_KEEP;
  const ttlDays = opts.ttlDays ?? QUARANTINE_TTL_DAYS;
  if (slots.length <= 1) return [];

  // ⛔ Most recent first BY TIME, not by letters. A lexical sort on the key
  // suffix is chronological only while every suffix is a well-formed ISO
  // stamp: an unreadable one like `not-a-date` sorts above every `2026-…`,
  // takes the "newest survives" protection for itself, and hands the real
  // newest slot to the reaper. Found by the test, not by reading.
  const at = (s: QuarantineSlot): number => {
    const t = Date.parse(s.quarantinedAt);
    // Unreadable ⇒ oldest. A stamp we wrote and can no longer read belongs to
    // a shape we no longer understand, and that is not the recent one.
    return Number.isNaN(t) ? -Infinity : t;
  };
  const ordered = [...slots].sort((a, b) => at(b) - at(a));

  const cutoff = now.getTime() - ttlDays * DAY_MS;
  const reap: string[] = [];

  for (let i = 0; i < ordered.length; i += 1) {
    const slot = ordered[i]!;
    // ⛔ The newest survives, always. Index 0 after the sort above.
    if (i === 0) continue;
    // ⛔ Completed work is the athlete's to discard, never ours.
    if (slot.hasCommittedWork) continue;
    const expired = at(slot) < cutoff;
    // Two independent reasons, either is enough: too old to mean anything, or
    // buried under enough newer slots that nobody will ever look at it.
    if (expired || i >= keep) reap.push(slot.key);
  }

  return reap;
}
